import type { Gear, Look } from "../types.ts";
import { drawFighter, figureHeight, gearView, REST_POSE, type GearView, type Pose } from "./fighter.ts";
import { clamp01, ease, ellipse, lerp, type Ctx } from "./util.ts";

export type AnimKind = "quick" | "normal" | "power" | "hit" | "block" | "dodge" | "taunt" | "roar" | "rest" | "throw" | "heal";

const DURATION: Record<AnimKind, number> = {
  quick: 0.45,
  normal: 0.65,
  power: 0.95,
  hit: 0.42,
  block: 0.45,
  dodge: 0.5,
  taunt: 1.15,
  roar: 1.0,
  rest: 1.0,
  throw: 0.7,
  heal: 0.9,
};

/** Fraction of the animation at which the blow lands / the egg leaves the wing. */
const IMPACT: Partial<Record<AnimKind, number>> = { quick: 0.5, normal: 0.5, power: 0.6, throw: 0.5, roar: 0.35 };

interface Anim {
  kind: AnimKind;
  t0: number;
  dur: number;
  resolve: () => void;
  impact?: () => void;
  fired: boolean;
}

interface Walk {
  from: number;
  to: number;
  t0: number;
  dur: number;
  resolve: () => void;
}

/** Ramp 0→1 over [a, b]. */
const seg = (p: number, a: number, b: number) => ease(clamp01((p - a) / (b - a)));

export class Actor {
  gear: GearView;
  state: "idle" | "ko" | "victory" = "idle";
  stunned = false;
  private anim: Anim | null = null;
  private walk: Walk | null = null;
  private koAt = 0;
  private flashAt = -10;
  private blinkAt = 2 + Math.random() * 3;

  constructor(
    public look: Look,
    gear: Gear,
    public facing: 1 | -1,
    public x: number,
  ) {
    this.gear = gearView(gear);
  }

  get height(): number {
    return figureHeight(this.look);
  }

  setGear(gear: Gear): void {
    this.gear = gearView(gear);
  }

  play(kind: AnimKind, now: number, impact?: () => void): Promise<void> {
    this.anim?.resolve();
    const { promise, resolve } = Promise.withResolvers<void>();
    this.anim = { kind, t0: now, dur: DURATION[kind], resolve, impact, fired: false };
    return promise;
  }

  walkTo(x: number, now: number, dur = 0.5): Promise<void> {
    this.walk?.resolve();
    const { promise, resolve } = Promise.withResolvers<void>();
    this.walk = { from: this.x, to: x, t0: now, dur, resolve };
    return promise;
  }

  flash(now: number): void {
    this.flashAt = now;
  }

  knockOut(now: number): void {
    this.state = "ko";
    this.koAt = now;
    this.stunned = false;
  }

  reset(): void {
    this.state = "idle";
    this.stunned = false;
    this.anim = null;
    this.walk = null;
  }

  private update(now: number): void {
    if (this.walk) {
      const p = clamp01((now - this.walk.t0) / this.walk.dur);
      this.x = lerp(this.walk.from, this.walk.to, ease(p));
      if (p >= 1) {
        const w = this.walk;
        this.walk = null;
        w.resolve();
      }
    }
    if (this.anim) {
      const p = (now - this.anim.t0) / this.anim.dur;
      const at = IMPACT[this.anim.kind];
      if (!this.anim.fired && at !== undefined && p >= at) {
        this.anim.fired = true;
        this.anim.impact?.();
      }
      if (p >= 1) {
        const a = this.anim;
        this.anim = null;
        if (!a.fired) a.impact?.();
        a.resolve();
      }
    }
  }

  /** Current pose plus root offsets (dx is along facing). */
  private pose(now: number): { pose: Pose; dx: number; dy: number; rot: number } {
    const pose: Pose = { ...REST_POSE };
    let dx = 0;
    let dy = 0;
    let rot = 0;
    pose.wing = 0.6 + Math.sin(now * 2.2) * 0.07;
    dy = Math.sin(now * 3) * 1.2;
    if (now > this.blinkAt) {
      pose.eyes = "closed";
      if (now > this.blinkAt + 0.12) this.blinkAt = now + 2 + Math.random() * 4;
    }

    if (this.walk) {
      pose.stride = 1;
      pose.walk = now * 16;
      dy = -Math.abs(Math.sin(now * 16)) * 4;
      pose.lean = 0.08 * Math.sign(this.walk.to - this.walk.from) * this.facing;
    }

    if (this.state === "ko") {
      const k = ease(clamp01((now - this.koAt) / 0.6));
      rot = -1.45 * k;
      dy = -18 * k - Math.sin(k * Math.PI) * 30;
      pose.eyes = "x";
      pose.wing = 1.6;
      pose.mouth = 1;
      return { pose, dx: -20 * k, dy, rot };
    }
    if (this.state === "victory" && !this.anim) {
      const hop = Math.abs(Math.sin(now * 5));
      dy = -hop * 22;
      pose.flap = hop;
      pose.mouth = hop;
      pose.wing = -1.2 + hop * 0.6;
      pose.squash = hop < 0.15 ? 0.6 : 0;
      return { pose, dx, dy, rot };
    }
    if (this.stunned) {
      pose.lean = Math.sin(now * 4) * 0.12;
      pose.eyes = "closed";
    }

    const a = this.anim;
    if (!a) return { pose, dx, dy, rot };
    const p = clamp01((now - a.t0) / a.dur);
    const bell = Math.sin(p * Math.PI);
    switch (a.kind) {
      case "quick": {
        const up = seg(p, 0, 0.35);
        const down = seg(p, 0.35, 0.55);
        const back = seg(p, 0.6, 1);
        pose.wing = lerp(lerp(lerp(0.6, -0.3, up), 1.5, down), 0.6, back);
        dx = lerp(lerp(0, 22, down), 0, back);
        pose.lean = lerp(lerp(0, 0.15, down), 0, back);
        pose.mouth = down * (1 - back);
        break;
      }
      case "normal": {
        const up = seg(p, 0, 0.4);
        const down = seg(p, 0.4, 0.55);
        const back = seg(p, 0.6, 1);
        pose.wing = lerp(lerp(lerp(0.6, -1.4, up), 1.7, down), 0.6, back);
        dx = lerp(lerp(lerp(0, -8, up), 34, down), 0, back);
        pose.lean = lerp(lerp(lerp(0, -0.12, up), 0.28, down), 0, back);
        pose.mouth = down * (1 - back);
        break;
      }
      case "power": {
        const up = seg(p, 0, 0.45);
        const down = seg(p, 0.48, 0.62);
        const back = seg(p, 0.7, 1);
        pose.wing = lerp(lerp(lerp(0.6, -2.3, up), 1.9, down), 0.6, back);
        dx = lerp(lerp(lerp(0, -12, up), 46, down), 0, back);
        dy = -Math.sin(clamp01((p - 0.25) / 0.37) * Math.PI) * 34;
        pose.lean = lerp(lerp(lerp(0, -0.25, up), 0.38, down), 0, back);
        pose.squash = p > 0.6 && p < 0.75 ? 1 : 0;
        pose.mouth = up;
        break;
      }
      case "hit":
        dx = -18 * Math.sin(Math.min(1, p * 2) * Math.PI * 0.5) * (1 - p);
        pose.lean = -0.3 * (1 - p);
        pose.eyes = p < 0.6 ? "closed" : "open";
        pose.mouth = 1 - p;
        pose.wing = 0.6 - 0.8 * bell;
        break;
      case "block":
        pose.guard = seg(p, 0, 0.2) * (1 - seg(p, 0.7, 1));
        dx = -8 * bell;
        pose.lean = -0.1 * bell;
        break;
      case "dodge":
        dx = -34 * bell;
        dy = -14 * bell;
        pose.lean = -0.4 * bell;
        break;
      case "taunt":
        dy = -Math.abs(Math.sin(p * Math.PI * 2)) * 20;
        pose.flap = Math.abs(Math.sin(p * Math.PI * 6));
        pose.mouth = 0.5 + 0.5 * Math.sin(p * Math.PI * 12);
        pose.lean = -0.12;
        break;
      case "roar": {
        const back = seg(p, 0, 0.3);
        const fwd = seg(p, 0.3, 0.45);
        const rel = seg(p, 0.85, 1);
        pose.lean = lerp(lerp(-0.3 * back, 0.3, fwd), 0, rel);
        pose.mouth = fwd * (1 - rel);
        pose.flap = fwd * (1 - rel) * (0.6 + 0.4 * Math.sin(now * 30));
        pose.squash = fwd * (1 - rel) * 0.4;
        break;
      }
      case "rest":
        pose.sit = bell;
        pose.eyes = "closed";
        pose.wing = 0.9;
        break;
      case "throw": {
        const up = seg(p, 0, 0.4);
        const down = seg(p, 0.4, 0.55);
        const back = seg(p, 0.6, 1);
        pose.wing = lerp(lerp(lerp(0.6, -2.1, up), 1.3, down), 0.6, back);
        pose.lean = lerp(lerp(-0.15 * up, 0.2, down), 0, back);
        break;
      }
      case "heal":
        dy = -bell * 10;
        pose.flap = Math.abs(Math.sin(p * Math.PI * 4));
        pose.eyes = "closed";
        break;
    }
    return { pose, dx, dy, rot };
  }

  draw(ctx: Ctx, now: number, ground: number, scale = 1): void {
    this.update(now);
    const { pose, dx, dy, rot } = this.pose(now);
    const x = this.x + dx * this.facing * scale;
    const s = (this.look.kind === "bird" ? this.look.size : 1) * scale;

    // ground shadow
    const lift = Math.min(1, -dy / 60);
    ellipse(ctx, x - (this.state === "ko" ? 40 * this.facing * scale : 0), ground + 2, (this.look.kind === "human" ? 44 : 40) * s * (1 - lift * 0.4) * (this.state === "ko" ? 1.5 : 1), 9 * s * (1 - lift * 0.4));
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.fill();

    ctx.save();
    ctx.translate(x, ground + dy * scale);
    ctx.scale(this.facing * s, s);
    ctx.rotate(rot);
    const flash = 1 - clamp01((now - this.flashAt) / 0.3);
    if (flash > 0) ctx.filter = `sepia(1) saturate(6) hue-rotate(-35deg) brightness(${1 + flash * 0.3})`;
    drawFighter(ctx, this.look, this.gear, pose, now);
    ctx.filter = "none";
    ctx.restore();

    if (this.stunned) {
      for (let i = 0; i < 3; i++) {
        const a = now * 4 + (i * Math.PI * 2) / 3;
        const sx = x + Math.cos(a) * 26 * s;
        const sy = ground - this.height * scale - 8 + Math.sin(a) * 7;
        ctx.font = "bold 20px sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "#ffe14d";
        ctx.strokeStyle = "#22160f";
        ctx.lineWidth = 3;
        ctx.strokeText("★", sx, sy);
        ctx.fillText("★", sx, sy);
      }
    }
  }
}
